import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import {
  Q7_1_OUTLETS,
  readQ7_1Answers,
  writeQ7_1Answers,
} from '@/lib/q7_1Storage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-[15px] font-medium leading-snug text-white/95 sm:text-[16px]';

const checkboxClass =
  'mt-0.5 h-[18px] w-[18px] shrink-0 rounded border border-slate-300/90 bg-white text-amber-800 focus:ring-1 focus:ring-slate-300';

export default function Q7_1() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [selected, setSelected] = useState<string[]>(() => readQ7_1Answers()?.selectedOutlets ?? []);
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) {
      return;
    }
    navigateAfterCheckRef.current = false;
    navigate('/7_2');
  }, [selected, navigate]);

  const toggleOutlet = (label: string) => {
    setSelected((prev) => {
      const wasChecked = prev.includes(label);
      const next = wasChecked ? [] : [label];
      writeQ7_1Answers({ selectedOutlets: next });
      if (!wasChecked && next.length > 0) {
        navigateAfterCheckRef.current = true;
      }
      return next;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.q7_1.question}
              </p>
              <div className="mt-5 flex flex-col gap-3">
                {Q7_1_OUTLETS.map((outlet) => {
                  const id = `q71-outlet-${outlet.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;
                  const checked = selected.includes(outlet);
                  return (
                    <label key={outlet} htmlFor={id} className={checkboxLabelClass}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOutlet(outlet)}
                        className={checkboxClass}
                      />
                      <span>{outlet}</span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-6 m-0 text-left text-[13px] italic leading-snug text-white/80 sm:text-[14px]">
                {t.q7_1.disclaimer}
              </p>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
